// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a ringed planet's rings on the immersive globe (headset VR and AR,
 * phone AR): one flat annulus, a child of the globe mesh so it turns,
 * tilts, scales and travels with it, lying in the globe's equatorial plane
 * (the x–z plane of THREE's SphereGeometry, whose poles are on ±y). Its
 * look is the shared profile of `planetRings.ts`, sampled by radius.
 *
 * Drawn unlit and see-through, both sides, without writing depth: the
 * globe writes depth first, so the half of the ring behind the planet is
 * hidden by it and the half in front draws over it, as the real thing.
 * Raycasts (vrInteraction) look at the globe meshes alone, never their
 * children, so the ring is never grabbed, pinned or read.
 */

import type * as THREE from 'three'
import { buildRingProfile, ringStrip, ringSystemOf } from './planetRings'

export const VR_PLANET_RINGS_NAME = 'planetRings'

export interface VrPlanetRingsHandle {
  /** The body whose rings show, lower case, or null for none. */
  readonly body: string | null
  /** The ring mesh while a ringed body shows, else null. */
  readonly mesh: THREE.Mesh | null
  /**
   * Show the rings of `body` (a `celestialBody` in any case, or
   * offEarthBody's lower-case name), or none for the Earth, a body without
   * rings or null. Idempotent: the same body again is a no-op.
   */
  setBody(body: string | null | undefined): void
  dispose(): void
}

export function createVrPlanetRings(
  THREE_: typeof THREE,
  globe: THREE.Mesh,
  globeRadius: number,
): VrPlanetRingsHandle {
  let body: string | null = null
  let mesh: THREE.Mesh | null = null
  let geometry: THREE.BufferGeometry | null = null
  let material: THREE.ShaderMaterial | null = null
  let profile: THREE.DataTexture | null = null

  function clear(): void {
    if (mesh) globe.remove(mesh)
    geometry?.dispose()
    material?.dispose()
    profile?.dispose()
    mesh = null
    geometry = null
    material = null
    profile = null
    body = null
  }

  function build(name: string): void {
    const system = ringSystemOf(name)
    if (!system) return
    const built = buildRingProfile(system)
    // sRGB values written as they are (no decode on sample, no encode on
    // output): what the table says is what the eye gets.
    profile = new THREE_.DataTexture(built.rgba, built.width, 1, THREE_.RGBAFormat)
    profile.colorSpace = THREE_.NoColorSpace
    // Mipmaps: a small or far globe sees the 2048-texel profile through a
    // few pixels, and without them the bands alias into moiré.
    profile.minFilter = THREE_.LinearMipmapLinearFilter
    profile.magFilter = THREE_.LinearFilter
    profile.wrapS = THREE_.ClampToEdgeWrapping
    profile.wrapT = THREE_.ClampToEdgeWrapping
    profile.generateMipmaps = true
    profile.needsUpdate = true

    const segments = 256
    const strip = ringStrip(system, segments)
    const count = (segments + 1) * 2
    const positions = new Float32Array(count * 3)
    const ts = new Float32Array(count)
    for (let i = 0; i < count; i++) {
      positions[i * 3] = strip[i * 4] * globeRadius
      positions[i * 3 + 1] = strip[i * 4 + 1] * globeRadius
      positions[i * 3 + 2] = strip[i * 4 + 2] * globeRadius
      ts[i] = strip[i * 4 + 3]
    }
    const index: number[] = []
    for (let s = 0; s < segments; s++) {
      const a = s * 2
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2)
    }
    geometry = new THREE_.BufferGeometry()
    geometry.setAttribute('position', new THREE_.BufferAttribute(positions, 3))
    geometry.setAttribute('ringT', new THREE_.BufferAttribute(ts, 1))
    geometry.setIndex(index)

    material = new THREE_.ShaderMaterial({
      uniforms: {
        uProfile: { value: profile },
      },
      vertexShader: `
        attribute float ringT;
        varying float vT;
        void main() {
          vT = ringT;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform sampler2D uProfile;
        varying float vT;
        void main() {
          vec4 c = texture2D(uProfile, vec2(vT, 0.5));
          if (c.a <= 0.002) discard;
          gl_FragColor = c;
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE_.DoubleSide,
    })
    mesh = new THREE_.Mesh(geometry, material)
    mesh.name = VR_PLANET_RINGS_NAME
    mesh.userData.body = system.body
    // After the globe's own surface and the overlay shells, which are opaque
    // or depth-tested at the surface: the ring must see their depth.
    mesh.renderOrder = 10
    mesh.frustumCulled = false
    globe.add(mesh)
    body = system.body
  }

  return {
    get body() {
      return body
    },
    get mesh() {
      return mesh
    },
    setBody(next) {
      const system = ringSystemOf(next)
      const name = system?.body ?? null
      if (name === body) return
      clear()
      if (name) build(name)
    },
    dispose() {
      clear()
    },
  }
}
